import React from 'react';
import PropTypes from 'prop-types';
import { HelpCircle, ChevronDown, User, Settings, LogOut, Home } from '@/lib/icons';

import Footer from './ui/Footer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { PORTAL_LINK, EDIT_PROFILE_LINK, CONTACT_LINK } from '../config';

/**
 * FacilitatorLayout - A reusable layout for all facilitator pages
 * Includes a standardized header and footer with consistent styling
 */
function getInitials(name) {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

const FacilitatorLayout = ({ children, userName = "User" }) => {
  const initials = getInitials(userName)
  return (
    <div className="flex flex-col min-h-screen">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-primary text-white">
        <div className="container mx-auto flex justify-between items-center h-16 px-4 md:px-6">
          {/* Logo and App name */}
          <div className="flex items-center space-x-4">
            <a href={PORTAL_LINK} className="shrink-0">
              <img src="/assets/prosci-portal-logo.svg" alt="Prosci" className="h-8 w-auto" />
            </a>
            <div className="hidden md:block text-xl font-thin">Change Success Assessment</div>
          </div>

          {/* Right side with user menu and help */}
          <div className="flex items-center gap-1 md:gap-2">
            <a href={CONTACT_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white hover:text-white/80 hover:bg-white/10 rounded-md transition-colors duration-200">
              <HelpCircle className="h-4 w-4" />
              Help
            </a>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white hover:text-white/80 hover:bg-white/10 rounded-md transition-colors duration-200 outline-none">
                  <span className="text-white"><User className="h-5 w-5" /></span>
                  <span>My Account</span>
                  <ChevronDown className="h-4 w-4 text-white" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <div className="px-4 py-3 border-b border-border bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="w-[34px] h-[34px] rounded-full bg-primary text-white flex items-center justify-center text-[12px] font-semibold flex-shrink-0">
                      {initials}
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-bold text-foreground leading-tight">{userName}</div>
                      <div className="text-xs text-muted-foreground leading-tight mt-0.5">Facilitator</div>
                    </div>
                  </div>
                </div>
                <div className="p-2">
                  <DropdownMenuItem asChild>
                    <a href={PORTAL_LINK} className="flex items-center gap-3 px-4 py-3 text-sm rounded-md w-full cursor-pointer text-popover-foreground">
                      <Home className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      Return to Portal
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href={EDIT_PROFILE_LINK} className="flex items-center gap-3 px-4 py-3 text-sm rounded-md w-full cursor-pointer text-popover-foreground">
                      <Settings className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      Edit Profile
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <a href="/sign_out" className="flex items-center gap-3 px-4 py-3 text-sm rounded-md w-full cursor-pointer text-popover-foreground">
                      <LogOut className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      Sign Out
                    </a>
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main content - add padding-top to account for fixed header */}
      <main className="flex-grow bg-muted pt-16">{children}</main>

      <Footer />
    </div>
  )
};

FacilitatorLayout.propTypes = {
  children: PropTypes.node.isRequired,
  userName: PropTypes.string,
};

export default FacilitatorLayout; 